import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TabComponent } from "@/components/tabs/TabComponent";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { ChevronDown, Dumbbell, Trash, UserCircle } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Hobbie, Profession } from "./schema/relation-fields.schema";
import { RelationFieldsFilter } from "@/components/common/section-components/filters/RelationFieldsFilter";
import { useLocation, useNavigate } from "react-router-dom";
import { FieldValues, useForm } from "react-hook-form";

import {
  clearFiltersHandler,
  fetchRelationFieldsSectionHandler,
  handleDeleteRelationFieldsHandler,
  submitFiltersRelationFieldsSection,
} from "./schema/handler-functions";
import { HobbiesTable } from "./sections/HobbiesTable";
import { ProfessionsTable } from "./sections/ProfessionsTable";
import { AppColors } from "@/styles/colors";
import { MenuComponent } from "@/components/menu/menu";
import { AppRoutes } from "@/constants/routes";
import { usePolling } from "@/hooks/usePolling";

export const RelationFieldsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const { state } = useLocation();
  const validTabs = new Set(["Hobbies", "Profesiones"]);
  const initialTab = validTabs.has(state?.tab) ? state.tab : "Hobbies";
  const [currentTab, setCurrentTab] = useState<string>(initialTab);

  useEffect(() => {
    if (state?.tab && validTabs.has(state.tab) && state.tab !== currentTab) {
      setCurrentTab(state.tab);
    }
  }, [state?.tab]);

  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);

  const [hobbies, setHobbies] = useState<Hobbie[] | undefined>(undefined);

  const [profesiones, setProfesiones] = useState<Profession[] | undefined>();

  const [currentFilters, setCurrentFilters] = useState<any>();

  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const { control, handleSubmit, getValues, setValue } = useForm();
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const { fetchPermissions, userPermissions } = useRoles();

  usePolling({
    callback: () => fetchSection(currentTab, { ...currentFilters }, false),
    delay: 10000,
    isActive,
  });


  const fetchSection = async (
    section: string,
    filters?: any,
    showLoad = true,
  ) => {
   await fetchRelationFieldsSectionHandler({
    setLoadingQuery,
    setHobbies,
    setProfesiones,
    filters,
    section,
    showLoad,
    navigate,
    fetchPermissions,
   })
  };

  useEffect(() => {
    if (!isActive) return;

    setFiltersApplied(false);
    setOpenFilters(false);
    setCurrentFilters({});
    setHobbiesSelection([]);
    setProfesionesSelection([]);
    setValue("name", "");

    fetchSection(currentTab);
  }, [currentTab, isActive]);

  const clearFilters = () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      currentSection: currentTab,
      setCurrentFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    submitFiltersRelationFieldsSection({
      event,
      fetchSection,
      setFiltersApplied,
      setOpenFilters,
      currentSection: currentTab,
      setCurrentFilters,
    });
  };

  const [hobbiesSelection, setHobbiesSelection] = useState<string[]>([]);
  const [profesionesSelection, setProfesionesSelection] = useState<string[]>(
    [],
  );

  const handleAddHobbie = () => {
    navigate(AppRoutes.HOBBIE_ADD);
  };

  const handleAddProfession = () => {
    navigate(AppRoutes.PROFESSION_ADD);
  };

  const { open, setOpen } = useDisclosure();
  const [loadingDeleteRelationFields, setLoadingDeleteRelationFields] =
    useState<boolean>(false);

  const getSelectionToSend = () => {
    switch (currentTab) {
      case "Hobbies":
        return hobbiesSelection;
      case "Profesiones":
        return profesionesSelection;
      default:
        return [];
    }
  };

  const handleDelete = async () => {
    handleDeleteRelationFieldsHandler({
      section: currentTab,
      selection: getSelectionToSend(),
      setLoadingDeleteRelationFields,
      setOpenConfirm: setOpen,
      fetchSection,
      setSelection:
        currentTab === "Hobbies"
          ? setHobbiesSelection
          : setProfesionesSelection,
    });
  };

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.FAMILY_RELATIONS_FIELDS &&
        p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="relation-fields-page"
    >
      <SectionHeader
        title="Campos relaciones familiares"
        //handleAdd={handleAddAssignation}
        showButtons={hasWriteAccess}
        aditionalButtons={
          <Flex gap="16px">
            <MenuComponent
              placement="bottom-end"
              trigger={
                <Button
                  bgColor={AppColors.GREEN_PRIMARY}
                  paddingX="10px"
                  disabled={loadingDeleteRelationFields}
                >
                  <Text>Agregar</Text>
                  <ChevronDown />
                </Button>
              }
              items={[
                {
                  label: "Hobbie",
                  value: "hobbie",
                  action: handleAddHobbie,
                },
                {
                  label: "Profesión",
                  value: "profesion",
                  action: handleAddProfession,
                },
              ]}
            />
            <Button
              paddingX="10px"
              onClick={() => setOpen(true)}
              disabled={
                loadingDeleteRelationFields ||
                (!hobbiesSelection?.length && !profesionesSelection.length)
              }
            >
              <Trash />
              Eliminar
            </Button>
          </Flex>
        }
      />

      <TabComponent
        onValueChange={(value) => setCurrentTab(value.value)}
        value={currentTab}
        tabs={[
          {
            label: "Hobbies",
            icon: <Dumbbell />,
            value: "Hobbies",
            content: (
              <HobbiesTable
                loadingQuery={loadingQuery}
                noActions={!hasWriteAccess}
                noSelection={!hasWriteAccess}
                setHobbiesSelection={setHobbiesSelection}
                sectionData={hobbies}
                hobbiesSelection={hobbiesSelection}
              />
            ),
          },
          {
            label: "Profesiones",
            icon: <UserCircle />,
            value: "Profesiones",
            content: (
              <ProfessionsTable
                loadingQuery={loadingQuery}
                noActions={!hasWriteAccess}
                noSelection={!hasWriteAccess}
                setProfessionSelection={setProfesionesSelection}
                sectionData={profesiones}
                professionSelection={profesionesSelection}
              />
            ),
          },
        ]}
        additionalButtons={
          <RelationFieldsFilter
            filtersApplied={filtersApplied}
            openFilters={openFilters}
            control={control}
            setOpenFilters={setOpenFilters}
            clearFilters={clearFilters}
            handleSubmit={handleSubmit}
            submitFilters={submitFilters}
          />
        }
      />

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={open}
          setOpenConfirm={setOpen}
          title={`Eliminar ${currentTab.toLocaleLowerCase()}`}
          subtitle="¿Está seguro de que desea realizar esta acción?"
          loadingAccept={loadingDeleteRelationFields}
          handleAccept={handleDelete}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
